// The fifteen badges, as the app shows them.
//
// What counts and how much is decided by the server only
// (supabase/migrations/20261012_badges.sql: badge_catalog and _badge_state);
// the app never computes a tier. This file holds what the server does not:
// the names, the line for each tier, and the art. Ids and order match
// badge_catalog. The art is one embroidered patch per badge painted in
// SwarmUI, with the border recoloured per tier to the Atlas metals
// (scripts/generate-badge-art.py).
import { supabase } from '../supabase';

export type BadgeId =
  | 'EXPLORER' | 'HOT_STREAK' | 'CLEAN_SWEEP' | 'SUMMIT' | 'QUICK_DRAW' | 'DEEP_DIVER'
  | 'SCHOLAR' | 'GRAND_TOUR' | 'DAWN_PATROL' | 'DEEP_CUT' | 'DUELIST' | 'TRAVEL_COMPANION'
  | 'NIGHT_OWL' | 'PHOTO_FINISH' | 'HOMECOMING';
export type Tier = 1 | 2 | 3;

export interface BadgeSpec {
  id: BadgeId;
  name: string;
  /** File stem under public/badges. */
  art: string;
  tiers: 1 | 3;
  /** Shown as a question mark until earned. */
  hidden: boolean;
  /** What each tier asks for; goals[0] is tier I. */
  goals: string[];
}

export const BADGES: BadgeSpec[] = [
  { id: 'EXPLORER', name: 'EXPLORER', art: 'explorer', tiers: 3, hidden: false,
    goals: ['Right answers in 5 realms', 'Right answers in all 10 realms', '10 right answers in every realm'] },
  { id: 'HOT_STREAK', name: 'HOT STREAK', art: 'hot-streak', tiers: 3, hidden: false,
    goals: ['5 right in a row in one game', '10 right in a row in one game', '20 right in a row in one game'] },
  { id: 'CLEAN_SWEEP', name: 'CLEAN SWEEP', art: 'clean-sweep', tiers: 3, hidden: false,
    goals: ['A game without a single miss', '5 games without a miss', '25 games without a miss'] },
  { id: 'SUMMIT', name: 'SUMMIT', art: 'summit', tiers: 3, hidden: false,
    goals: ['8 of 10 on a Climb', '9 of 10 on a Climb', '10 of 10 on a Climb'] },
  { id: 'QUICK_DRAW', name: 'QUICK DRAW', art: 'quick-draw', tiers: 3, hidden: false,
    goals: ['10 right answers in under 3 seconds', '50 right answers in under 3 seconds', '250 right answers in under 3 seconds'] },
  { id: 'DEEP_DIVER', name: 'DEEP DIVER', art: 'deep-diver', tiers: 3, hidden: false,
    goals: ['Question 10 in Survival', 'Question 20 in Survival', 'Question 30 in Survival'] },
  { id: 'SCHOLAR', name: 'SCHOLAR', art: 'scholar', tiers: 3, hidden: false,
    goals: ['100 right answers', '1,000 right answers', '10,000 right answers'] },
  { id: 'GRAND_TOUR', name: 'GRAND TOUR', art: 'grand-tour', tiers: 1, hidden: false,
    goals: ['Rush, Climb, Survival and the Daily, all played'] },
  { id: 'DAWN_PATROL', name: 'DAWN PATROL', art: 'dawn-patrol', tiers: 3, hidden: false,
    goals: ['The Daily, 2 days in a row', 'The Daily, 7 days in a row', 'The Daily, 30 days in a row'] },
  { id: 'DEEP_CUT', name: 'DEEP CUT', art: 'deep-cut', tiers: 3, hidden: false,
    goals: ['5 hard questions right', '25 hard questions right', '100 hard questions right'] },
  { id: 'DUELIST', name: 'DUELIST', art: 'duelist', tiers: 3, hidden: false,
    goals: ['Your first 1 VS 1 win', '10 wins in 1 VS 1', '50 wins in 1 VS 1'] },
  { id: 'TRAVEL_COMPANION', name: 'TRAVEL COMPANION', art: 'travel-companion', tiers: 3, hidden: false,
    goals: ['A friend joined you from your invite', '5 friends joined from your invites', '20 friends joined from your invites'] },
  { id: 'NIGHT_OWL', name: 'NIGHT OWL', art: 'night-owl', tiers: 1, hidden: true, goals: ['A game after midnight'] },
  { id: 'PHOTO_FINISH', name: 'PHOTO FINISH', art: 'photo-finish', tiers: 1, hidden: true, goals: ['A 1 VS 1 won by under 100 points'] },
  { id: 'HOMECOMING', name: 'HOMECOMING', art: 'homecoming', tiers: 1, hidden: true, goals: ['Back after a week away'] },
];

const BY_ID = new Map(BADGES.map((b) => [b.id, b]));
export const badgeSpec = (id: BadgeId): BadgeSpec => BY_ID.get(id)!;

/** The Atlas Rank metals (lib/atlasRank.ts): VOYAGER, NAVIGATOR, PIONEER. Bronze
 *  text runs a step lighter than the metal, which is too dark to read on the card. */
export const TIER_LABEL: Record<Tier, string> = { 1: 'BRONZE', 2: 'SILVER', 3: 'GOLD' };
export const TIER_NUMERAL: Record<Tier, string> = { 1: 'I', 2: 'II', 3: 'III' };
export const TIER_METAL: Record<Tier, string> = { 1: '#b0764a', 2: '#dde3ee', 3: '#e2b13c' };
export const TIER_TEXT: Record<Tier, string> = { 1: '#E2A579', 2: '#DDE3EE', 3: '#E2B13C' };

/** "BRONZE · I"; a single-tier badge has no ladder, so no label. */
export function tierLabel(spec: BadgeSpec, tier: Tier): string | null {
  return spec.tiers === 1 ? null : `${TIER_LABEL[tier]} · ${TIER_NUMERAL[tier]}`;
}

/** The patch for a tier; a locked badge shows its first tier, dimmed by the caller. */
export function badgeArt(id: BadgeId, tier: Tier | 0): string {
  const spec = badgeSpec(id);
  const file = spec.tiers === 1 ? `${spec.art}.webp` : `${spec.art}-${Math.max(1, tier)}.webp`;
  return `${import.meta.env.BASE_URL}badges/${file}`;
}

// ── What the server says ─────────────────────────────────────────────

export interface BadgeState {
  id: BadgeId;
  /** 0 = not earned yet. */
  tier: Tier | 0;
  /** The count toward the next tier (EXPLORER's switches to realms with ten right at III). */
  have: number;
  /** The next tier's threshold; null at the top. */
  need: number | null;
  /** Per cent of real players who hold this tier or higher; null while unearned. */
  held: number | null;
}

export interface BadgeSync {
  badges: BadgeState[];
  /** Earned or upgraded by this count, rarest first. */
  fresh: { id: BadgeId; tier: Tier }[];
}

/** The next tier's line and numbers ("Silver: 5 games without a miss", 1 of 5), or
 *  null at the top and for a hidden badge not yet earned. */
export function nextStep(s: BadgeState): { label: string; have: number; need: number } | null {
  const spec = badgeSpec(s.id);
  if (s.need === null || s.tier >= spec.tiers || (spec.hidden && s.tier === 0)) return null;
  const next = (s.tier + 1) as Tier;
  const label = spec.tiers === 1 ? spec.goals[0]
    : `${TIER_LABEL[next][0]}${TIER_LABEL[next].slice(1).toLowerCase()}: ${spec.goals[next - 1]}`;
  return { label, have: Math.min(s.have, s.need), need: s.need };
}

/** The line under an earned badge: what its current tier took. */
export function earnedLine(id: BadgeId, tier: Tier): string {
  const spec = badgeSpec(id);
  return `${spec.goals[Math.min(tier, spec.tiers) - 1]}.`;
}

/** "Only 4% of players have it." Said only when it is a brag: under 10%. */
export function rarityLine(held: number | null): string | null {
  if (held === null || held >= 10) return null;
  if (held < 1) return 'Fewer than 1 in 100 players have it.';
  return `Only ${Math.round(held)}% of players have it.`;
}

export function parseBadgeSync(raw: unknown): BadgeSync | null {
  const r = raw as { badges?: any[]; new?: any[] } | null;
  if (!r || !Array.isArray(r.badges)) return null;
  // A badge the server knows and this build does not is skipped, not shown blank.
  const badges: BadgeState[] = BADGES.map((spec) => {
    const s = r.badges!.find((b) => b?.id === spec.id);
    return {
      id: spec.id,
      tier: (s && [1, 2, 3].includes(s.tier) ? s.tier : 0) as Tier | 0,
      have: Number.isFinite(s?.have) ? s.have : 0,
      need: Number.isFinite(s?.need) ? s.need : null,
      held: Number.isFinite(s?.held) ? s.held : null,
    };
  });
  const heldOf = (id: BadgeId) => badges.find((b) => b.id === id)?.held ?? 100;
  const fresh = (r.new ?? [])
    .filter((b) => BY_ID.has(b?.id) && [1, 2, 3].includes(b?.tier))
    .map((b) => ({ id: b.id as BadgeId, tier: b.tier as Tier }))
    .sort((a, b) => heldOf(a.id) - heldOf(b.id)
      || BADGES.findIndex((x) => x.id === a.id) - BADGES.findIndex((x) => x.id === b.id));
  return { badges, fresh };
}

/** Minutes east of UTC; the server only uses it for NIGHT_OWL. */
const tzOffsetMin = () => -new Date().getTimezoneOffset();

// Dev builds talk to the production database, and a test game there would
// write a real player's history. ?badges=demo shows a fixed state instead,
// with nothing sent.
const DEMO = import.meta.env.DEV && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('badges') === 'demo';

/** The count when a game ends, from the seat it was played from. */
export async function awardBadges(seatId: string): Promise<BadgeSync | null> {
  if (DEMO) return demoSync(true);
  try {
    const { data, error } = await supabase.rpc('award_badges', { p_seat: seatId, p_tz_offset_min: tzOffsetMin() });
    return error ? null : parseBadgeSync(data);
  } catch {
    return null;
  }
}

/** Profile's shelf, for the player lib/playerId.ts resolves. */
export async function loadBadges(playerId: string | null): Promise<BadgeSync | null> {
  if (DEMO) return demoSync(false);
  if (!playerId) return parseBadgeSync({ badges: [], new: [] });
  try {
    const { data, error } = await supabase.rpc('badges_for_player', { p_player: playerId, p_tz_offset_min: tzOffsetMin() });
    return error ? null : parseBadgeSync(data);
  } catch {
    return null;
  }
}

function demoSync(withNew: boolean): BadgeSync {
  const s = (id: BadgeId, tier: Tier | 0, have: number, need: number | null, held: number | null) => ({ id, tier, have, need, held });
  return parseBadgeSync({
    badges: [
      s('EXPLORER', 2, 3, 10, 31), s('HOT_STREAK', 1, 7, 10, 72.2), s('CLEAN_SWEEP', 1, 1, 5, 49.5),
      s('SUMMIT', 2, 9, 10, 18.8), s('QUICK_DRAW', 0, 4, 10, null), s('DEEP_DIVER', 1, 12, 20, 8.6),
      s('SCHOLAR', 0, 62, 100, null), s('GRAND_TOUR', 0, 2, 4, null), s('DAWN_PATROL', 0, 1, 2, null),
      s('DEEP_CUT', 0, 1, 5, null), s('DUELIST', 0, 0, 1, null), s('TRAVEL_COMPANION', 0, 0, 1, null),
      s('NIGHT_OWL', 1, 1, null, 12.2), s('PHOTO_FINISH', 0, 0, 1, null), s('HOMECOMING', 0, 2, 7, null),
    ],
    new: withNew ? [{ id: 'CLEAN_SWEEP', tier: 1 }, { id: 'DEEP_DIVER', tier: 1 }] : [],
  })!;
}
